import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Loader2, Lock, NotebookPen, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import cultivarService from '../../services/cultivar';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import type { CultivarSalesNote } from '../../types/cultivar';

interface CultivarSalesNotesProps {
  cultivarId: number;
}

/**
 * The organisation's own sales notes on a cultivar: who to pitch it to,
 * supply, price guidance, what customers ask. Nothing here is about growing
 * it, and nobody outside the organisation reads it, the cultivar's owner
 * included. Any Cultivar or Sales seat adds a note; only its author changes it.
 */
export default function CultivarSalesNotes({ cultivarId }: CultivarSalesNotesProps) {
  const tz = useOrgTimezone();
  const [notes, setNotes] = useState<CultivarSalesNote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await cultivarService.listSalesNotes(cultivarId);
      if (!res.success) throw res;
      setNotes(res.data ?? []);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the sales notes'));
      setNotes((n) => n ?? []);
    }
  }, [cultivarId]);

  useEffect(() => { load(); }, [load]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const res = await cultivarService.addSalesNote(cultivarId, draft);
      if (!res.success || !res.data) throw res;
      const added = res.data;
      setNotes((n) => [added, ...(n ?? [])]);
      setDraft('');
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not save the note'));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (note: CultivarSalesNote) => {
    if (!editText.trim()) return;
    setSaving(true);
    try {
      const res = await cultivarService.updateSalesNote(cultivarId, note.id, editText);
      if (!res.success || !res.data) throw res;
      const updated = res.data;
      setNotes((n) => (n ?? []).map((x) => (x.id === note.id ? updated : x)));
      setEditingId(null);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not save the note'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (note: CultivarSalesNote) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const res = await cultivarService.deleteSalesNote(cultivarId, note.id);
      if (!res.success) throw res;
      setNotes((n) => (n ?? []).filter((x) => x.id !== note.id));
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not delete the note'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <NotebookPen className="h-5 w-5 text-primary-600" aria-hidden />
          Sales notes
        </CardTitle>
        <p className="mt-1 flex items-start gap-1.5 text-sm text-neutral-500">
          <Lock className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
          <span>Your organisation&apos;s own notes for selling this cultivar. Nobody outside your organisation sees them.</span>
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={add} className="space-y-2">
          <label htmlFor={`sales-note-new-${cultivarId}`} className="sr-only">New sales note</label>
          <textarea
            id={`sales-note-new-${cultivarId}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={8000}
            placeholder="Who to pitch it to, supply, price guidance, what customers ask…"
            className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
          <div className="flex justify-end">
            <Button type="submit" variant="primary" size="sm" disabled={saving || !draft.trim()}>
              {saving && editingId === null ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              Add note
            </Button>
          </div>
        </form>

        {error && <p className="mt-3 text-sm text-error-600 break-words" role="alert">{error}</p>}

        {notes === null ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 text-primary-600 animate-spin" aria-label="Loading" /></div>
        ) : notes.length === 0 ? (
          <p className="mt-4 text-sm text-neutral-500">No sales notes yet.</p>
        ) : (
          <ul className="mt-4 divide-y divide-neutral-100">
            {notes.map((note) => (
              <li key={note.id} className="py-3 min-w-0">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="text-xs text-neutral-500 break-words min-w-0">
                    <span className="font-medium text-neutral-700">{note.author_name || 'Someone no longer here'}</span>
                    {' · '}{formatDateTimeInTz(note.created_at, tz)}
                    {note.updated_at && <> · edited {formatDateTimeInTz(note.updated_at, tz)}</>}
                  </p>
                  {note.mine && editingId !== note.id && (
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" aria-label="Edit note"
                        onClick={() => { setEditingId(note.id); setEditText(note.body); }}>
                        <Pencil className="h-4 w-4" aria-hidden />
                      </Button>
                      <Button variant="ghost" size="sm" aria-label="Delete note" onClick={() => remove(note)}>
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </div>
                  )}
                </div>
                {editingId === note.id ? (
                  <div className="mt-2 space-y-2">
                    <label htmlFor={`sales-note-edit-${note.id}`} className="sr-only">Edit sales note</label>
                    <textarea
                      id={`sales-note-edit-${note.id}`}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                      maxLength={8000}
                      className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingId(null)} disabled={saving}>Cancel</Button>
                      <Button variant="primary" size="sm" onClick={() => saveEdit(note)} disabled={saving || !editText.trim()}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-neutral-800 whitespace-pre-wrap break-words">{note.body}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
